import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, CheckCircle2, XCircle, Clock, Loader2, Maximize2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Declined" },
  { key: "", label: "All" },
];

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: Clock },
  approved: { label: "Approved", cls: "bg-green-100 text-green-800 border-green-300", Icon: CheckCircle2 },
  rejected: { label: "Declined", cls: "bg-red-100 text-red-700 border-red-300", Icon: XCircle },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export function AgeVerificationManager({ onChanged }) {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/age-verifications", { params: filter ? { status: filter } : {} });
      setRows(data || []);
    } catch { setRows([]); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/age-verifications/${uid}/approve`);
      toast.success("Age verification approved");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy("");
  };

  const reject = async () => {
    const uid = rejectFor;
    setBusy(uid);
    try {
      await api.post(`/age-verifications/${uid}/reject`, { note: note.trim() || null });
      toast.success("Age verification declined");
      setRejectFor(null); setNote("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.key || "all"} data-testid={`age-filter-${f.key || "all"}`} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No age-verification submissions{filter ? ` (${filter})` : ""}.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Buyer</TableHead>
                <TableHead>ID document</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const av = r.age_verification || {};
                const sb = STATUS_BADGE[av.status] || STATUS_BADGE.pending;
                return (
                  <TableRow key={r.user_id} data-testid={`age-row-${r.user_id}`}>
                    <TableCell>
                      <p className="font-medium">{r.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{r.email}</p>
                    </TableCell>
                    <TableCell>
                      {av.doc_url ? (
                        <button onClick={() => setViewDoc(av.doc_url)} data-testid={`age-doc-${r.user_id}`}
                          className="relative group w-16 h-12 rounded-md overflow-hidden border">
                          <img src={av.doc_url} alt="ID" className="w-full h-full object-cover" />
                          <span className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Maximize2 className="w-4 h-4 text-white" /></span>
                        </button>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmtDate(av.submitted_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${sb.cls}`}><sb.Icon className="w-3 h-3" />{sb.label}</Badge>
                      {av.status === "rejected" && av.note && <p className="text-[11px] text-muted-foreground mt-1 max-w-[160px] truncate">{av.note}</p>}
                    </TableCell>
                    <TableCell className="text-right">
                      {av.status !== "approved" && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" data-testid={`age-approve-${r.user_id}`} disabled={busy === r.user_id} onClick={() => approve(r.user_id)}>
                            {busy === r.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve"}
                          </Button>
                          {av.status !== "rejected" && (
                            <Button size="sm" variant="outline" data-testid={`age-reject-${r.user_id}`} disabled={busy === r.user_id} onClick={() => { setRejectFor(r.user_id); setNote(""); }}>Decline</Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>ID document</DialogTitle></DialogHeader>
          {viewDoc && <img src={viewDoc} alt="ID document" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline age verification</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Optionally tell the buyer why (they'll be asked to re-upload).</p>
          <Textarea data-testid="age-reject-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Photo is blurry / ID expired" rows={3} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="age-reject-confirm" disabled={busy === rejectFor} onClick={reject}>
              {busy === rejectFor ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decline"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
